export interface PurchasedPackCaptureMedia {
  id: string;
  capturedAt: string | null;
  poseName: string | null;
  poseIndex: number | null;
  cameraId: string | null;
  cameraLabel: string | null;
}

export interface PurchasedPackRecordingMedia {
  id: string;
  label: string | null;
  poseName: string | null;
  poseIndex: number | null;
  cameraId: string | null;
  cameraLabel: string | null;
  status: string;
  hasVideo: boolean;
  contentType: string | null;
  durationSeconds: number | null;
  startedAt: string | null;
  endedAt: string | null;
}

export interface RecordingLabelParts {
  poseName: string | null;
  cameraLabel: string | null;
}

export interface ReferencePackCameraColumn {
  key: string;
  label: string;
}

export interface ReferencePackPoseCell {
  cameraKey: string;
  cameraLabel: string;
  captures: PurchasedPackCaptureMedia[];
  recordings: PurchasedPackRecordingMedia[];
}

export interface ReferencePackPoseRow {
  key: string;
  label: string;
  timestampMs: number | null;
  cells: ReferencePackPoseCell[];
}

interface MutablePoseGroup {
  key: string;
  label: string;
  labelKey: string | null;
  minTimestampMs: number | null;
  maxTimestampMs: number | null;
  cells: Map<string, ReferencePackPoseCell>;
}

function normalizeLabel(value: string | null | undefined) {
  const normalized = value?.trim().replace(/\s+/g, ' ').toLowerCase();
  return normalized || null;
}

function getTimestampMs(value: string | null | undefined) {
  if (!value) return null;
  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) ? timestamp : null;
}

function getCameraLabel(cameraLabel: string | null | undefined, fallback = 'Unlabeled camera') {
  return cameraLabel?.trim() || fallback;
}

function getCameraKey(cameraLabel: string | null | undefined, cameraId?: string | null) {
  const normalizedCameraLabel = normalizeLabel(cameraLabel);
  if (normalizedCameraLabel) return `label:${normalizedCameraLabel}`;
  if (cameraId?.trim()) return `id:${cameraId.trim()}`;
  return 'camera:unlabeled';
}

function getPoseLabel(capture: PurchasedPackCaptureMedia) {
  const poseName = capture.poseName?.trim();
  if (poseName) return poseName;
  if (capture.poseIndex !== null && capture.poseIndex !== undefined) return `Pose ${capture.poseIndex + 1}`;
  return 'Unlabeled pose';
}

function getCapturePoseKey(capture: PurchasedPackCaptureMedia) {
  if (capture.poseIndex !== null && capture.poseIndex !== undefined) return `pose-index:${capture.poseIndex}`;
  const poseLabel = normalizeLabel(capture.poseName);
  if (poseLabel) return `pose-label:${poseLabel}`;
  return 'pose:unlabeled';
}

function getRecordingPoseLabel(recording: PurchasedPackRecordingMedia, labelParts: RecordingLabelParts) {
  const poseName = recording.poseName?.trim() || labelParts.poseName?.trim();
  if (poseName) return poseName;
  if (recording.poseIndex !== null && recording.poseIndex !== undefined) return `Pose ${recording.poseIndex + 1}`;
  return 'Recorded pose';
}

function getRecordingPoseKey(recording: PurchasedPackRecordingMedia) {
  if (recording.poseIndex !== null && recording.poseIndex !== undefined) return `pose-index:${recording.poseIndex}`;
  const poseLabel = normalizeLabel(recording.poseName);
  if (poseLabel) return `pose-label:${poseLabel}`;
  return null;
}

function compareCameraColumns(a: ReferencePackCameraColumn, b: ReferencePackCameraColumn) {
  const aCameraMatch = a.label.match(/^camera\s+([a-z]+)$/i);
  const bCameraMatch = b.label.match(/^camera\s+([a-z]+)$/i);

  if (aCameraMatch && bCameraMatch) {
    return cameraLettersToNumber(aCameraMatch[1]) - cameraLettersToNumber(bCameraMatch[1]);
  }

  if (aCameraMatch) return -1;
  if (bCameraMatch) return 1;

  return a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' });
}

function cameraLettersToNumber(value: string) {
  return value.toUpperCase().split('').reduce((total, letter) => {
    const charCode = letter.charCodeAt(0);
    if (charCode < 65 || charCode > 90) return total;
    return total * 26 + (charCode - 64);
  }, 0);
}

function getOrCreateCell(
  group: MutablePoseGroup,
  cameraKey: string,
  cameraLabel: string
): ReferencePackPoseCell {
  let cell = group.cells.get(cameraKey);
  if (!cell) {
    cell = {
      cameraKey,
      cameraLabel,
      captures: [],
      recordings: [],
    };
    group.cells.set(cameraKey, cell);
  }
  return cell;
}

function updateGroupTimestamp(group: MutablePoseGroup, timestampMs: number | null) {
  if (timestampMs === null) return;
  group.minTimestampMs = group.minTimestampMs === null ? timestampMs : Math.min(group.minTimestampMs, timestampMs);
  group.maxTimestampMs = group.maxTimestampMs === null ? timestampMs : Math.max(group.maxTimestampMs, timestampMs);
}

function findBestGroupForRecording(
  groups: MutablePoseGroup[],
  labelParts: RecordingLabelParts,
  recordingStartMs: number | null,
  recordingEndMs: number | null
) {
  const poseLabelKey = normalizeLabel(labelParts.poseName);
  const labelCandidates = poseLabelKey
    ? groups.filter((group) => group.labelKey === poseLabelKey)
    : [];

  if (labelCandidates.length === 1) return labelCandidates[0];
  if (labelCandidates.length > 1 && recordingStartMs !== null) {
    return [...labelCandidates].sort((a, b) => (
      getGroupDistanceFromTimestamp(a, recordingStartMs) - getGroupDistanceFromTimestamp(b, recordingStartMs)
    ))[0];
  }

  if (recordingStartMs !== null && recordingEndMs !== null) {
    const overlappingGroup = groups.find((group) => {
      if (group.minTimestampMs === null) return false;
      return group.minTimestampMs >= recordingStartMs - 1000 && group.minTimestampMs <= recordingEndMs + 1000;
    });
    if (overlappingGroup) return overlappingGroup;
  }

  if (poseLabelKey && recordingStartMs !== null) {
    const nearbyRecordingGroup = groups.find((group) => (
      group.key.startsWith(`recording-pose:${poseLabelKey}:`)
      && group.minTimestampMs !== null
      && Math.abs(group.minTimestampMs - recordingStartMs) < 2 * 60 * 1000
    ));
    if (nearbyRecordingGroup) return nearbyRecordingGroup;
  }

  return null;
}

function getGroupDistanceFromTimestamp(group: MutablePoseGroup, timestampMs: number) {
  if (group.minTimestampMs === null) return Number.MAX_SAFE_INTEGER;
  return Math.abs(group.minTimestampMs - timestampMs);
}

function createRecordingPoseGroup(
  groupsByKey: Map<string, MutablePoseGroup>,
  labelParts: RecordingLabelParts,
  recordingStartMs: number | null,
  recordingId: string,
  recording?: PurchasedPackRecordingMedia
) {
  const structuredPoseKey = recording ? getRecordingPoseKey(recording) : null;
  const poseLabel = recording
    ? getRecordingPoseLabel(recording, labelParts)
    : labelParts.poseName?.trim() || 'Recorded pose';
  const poseLabelKey = normalizeLabel(poseLabel);
  const timestampBucket = recordingStartMs === null ? recordingId : String(Math.floor(recordingStartMs / (2 * 60 * 1000)));
  const key = structuredPoseKey ?? `recording-pose:${poseLabelKey ?? 'unlabeled'}:${timestampBucket}`;
  let group = groupsByKey.get(key);

  if (!group) {
    group = {
      key,
      label: poseLabel,
      labelKey: poseLabelKey,
      minTimestampMs: recordingStartMs,
      maxTimestampMs: recordingStartMs,
      cells: new Map(),
    };
    groupsByKey.set(key, group);
  }

  return group;
}

export function parseRecordingLabel(label: string | null | undefined): RecordingLabelParts {
  const trimmedLabel = label?.trim();
  if (!trimmedLabel) {
    return { poseName: null, cameraLabel: null };
  }

  const parentheticalMatch = trimmedLabel.match(/^(.+?)\s*\(([^()]+)\)\s*$/);
  if (!parentheticalMatch) {
    return { poseName: trimmedLabel, cameraLabel: null };
  }

  return {
    poseName: parentheticalMatch[1].trim() || null,
    cameraLabel: parentheticalMatch[2].trim() || null,
  };
}

export function buildReferencePackPoseRows(
  captures: PurchasedPackCaptureMedia[],
  recordings: PurchasedPackRecordingMedia[]
) {
  const groupsByKey = new Map<string, MutablePoseGroup>();
  const cameraColumnsByKey = new Map<string, ReferencePackCameraColumn>();

  captures.forEach((capture) => {
    const poseKey = getCapturePoseKey(capture);
    let group = groupsByKey.get(poseKey);
    const poseLabel = getPoseLabel(capture);
    const timestampMs = getTimestampMs(capture.capturedAt);

    if (!group) {
      group = {
        key: poseKey,
        label: poseLabel,
        labelKey: normalizeLabel(poseLabel),
        minTimestampMs: timestampMs,
        maxTimestampMs: timestampMs,
        cells: new Map(),
      };
      groupsByKey.set(poseKey, group);
    } else {
      updateGroupTimestamp(group, timestampMs);
    }

    const cameraLabel = getCameraLabel(capture.cameraLabel);
    const cameraKey = getCameraKey(capture.cameraLabel, capture.cameraId);
    cameraColumnsByKey.set(cameraKey, { key: cameraKey, label: cameraLabel });
    getOrCreateCell(group, cameraKey, cameraLabel).captures.push(capture);
  });

  recordings.forEach((recording) => {
    const labelParts = parseRecordingLabel(recording.label);
    const recordingStartMs = getTimestampMs(recording.startedAt);
    const recordingEndMs = getTimestampMs(recording.endedAt);
    const recordingPoseKey = getRecordingPoseKey(recording);
    const group = (recordingPoseKey ? groupsByKey.get(recordingPoseKey) ?? null : null)
      ?? findBestGroupForRecording(
        Array.from(groupsByKey.values()),
        {
          poseName: recording.poseName ?? labelParts.poseName,
          cameraLabel: recording.cameraLabel ?? labelParts.cameraLabel,
        },
        recordingStartMs,
        recordingEndMs
      )
      ?? createRecordingPoseGroup(groupsByKey, labelParts, recordingStartMs, recording.id, recording);

    updateGroupTimestamp(group, recordingStartMs);
    updateGroupTimestamp(group, recordingEndMs);

    const cameraLabel = getCameraLabel(recording.cameraLabel ?? labelParts.cameraLabel);
    const cameraKey = getCameraKey(recording.cameraLabel ?? labelParts.cameraLabel, recording.cameraId);
    cameraColumnsByKey.set(cameraKey, { key: cameraKey, label: cameraLabel });
    getOrCreateCell(group, cameraKey, cameraLabel).recordings.push(recording);
  });

  const cameraColumns = Array.from(cameraColumnsByKey.values()).sort(compareCameraColumns);

  const rows = Array.from(groupsByKey.values())
    .sort((a, b) => {
      if (a.minTimestampMs !== null && b.minTimestampMs !== null) return a.minTimestampMs - b.minTimestampMs;
      if (a.minTimestampMs !== null) return -1;
      if (b.minTimestampMs !== null) return 1;
      return a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' });
    })
    .map<ReferencePackPoseRow>((group) => ({
      key: group.key,
      label: group.label,
      timestampMs: group.minTimestampMs,
      cells: cameraColumns.map((camera) => group.cells.get(camera.key) ?? {
        cameraKey: camera.key,
        cameraLabel: camera.label,
        captures: [],
        recordings: [],
      }),
    }));

  return {
    cameraColumns,
    rows,
  };
}
